import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import { Alert, Button, Drawer, Space, Spin, Tag } from "antd";

import TransactionService from "../../../../../servics/notification/transaction/TransactionService";

import {
    ACTIVE_PAYMENT_STATUSES,
    PAYMENT_STATUS_LABELS,
    RETRYABLE_PAYMENT_STATUSES,
    apiErrorMessage,
    formatAmount,
    newIdempotencyKey
} from "../../../../../constants/marketplaceCheckout";
import useCurrency from "../../../../../hooks/useCurrency";

import DeliverySection, { EMPTY_ADDRESS, missingAddressFields, requiresAddress } from "./checkout/DeliverySection";
import OrderSummary from "./checkout/OrderSummary";
import PaymentMethodSection, { mobileMoneyError } from "./checkout/PaymentMethodSection";
import PaymentStatusPanel, { OrderConfirmation } from "./checkout/PaymentStatusPanel";

const POLL_INTERVAL_MS = 4000;

const addressFrom = (checkoutAddress, fallbackCountry) => ({
    ...EMPTY_ADDRESS,
    ...(checkoutAddress || {}),
    country: checkoutAddress?.country || fallbackCountry || null,
    landmark: checkoutAddress?.landmark || "",
    additionalInformation: checkoutAddress?.additionalInformation || "",
    contactPhone: checkoutAddress?.contactPhone || ""
});

/**
 * Parcours post-Agreement :
 *
 *   Livraison → Adresse (si nécessaire) → Devis / partenaire → Récapitulatif
 *   → Moyen de paiement → (opérateur + numéro) → Payer
 *
 * « Payer » confirme le checkout (PUT, devis relu et montants figés côté serveur)
 * puis initie le paiement (POST, Idempotency-Key). La commande n'existe qu'après
 * confirmation du paiement par le serveur ; elle est alors affichée avec le suivi
 * de livraison. L'Agreement n'est jamais modifié par ce parcours.
 */
const PaymentFinalizationDrawer = ({ open = false, agreement = null, currentUserId = null, onClose, onPaymentSettled }) => {
    const [catalog, setCatalog] = useState(null);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [catalogError, setCatalogError] = useState(null);

    const [deliveryType, setDeliveryType] = useState(null);
    const [address, setAddress] = useState(EMPTY_ADDRESS);
    const [quotes, setQuotes] = useState(null);
    const [quotesLoading, setQuotesLoading] = useState(false);
    const [quotesError, setQuotesError] = useState(null);
    const [selectedOptionId, setSelectedOptionId] = useState(null);

    const [paymentMethod, setPaymentMethod] = useState(null);
    const [operator, setOperator] = useState(null);
    const [phone, setPhone] = useState("");

    const [payment, setPayment] = useState(null);
    const [lastFailedPayment, setLastFailedPayment] = useState(null);
    const [order, setOrder] = useState(null);
    const [delivery, setDelivery] = useState(null);
    const [initializing, setInitializing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const idempotencyKeyRef = useRef(null);
    const settledRef = useRef(null);

    const inquiryId = agreement?.inquiryId ?? null;
    const agreementId = agreement?.id ?? null;
    const { currency: contextCurrency } = useCurrency();
    const currency = agreement?.currency || contextCurrency;
    const isBuyer = !currentUserId || String(agreement?.buyerId) === String(currentUserId);

    const loadCatalog = useCallback(async () => {
        setCatalogLoading(true);
        setCatalogError(null);
        try {
            const options = await TransactionService.getPaymentOptions(agreement?.currency);
            setCatalog(options);
            return options;
        } catch (requestError) {
            setCatalog(null);
            setCatalogError(apiErrorMessage(requestError, "Impossible de charger les moyens de paiement."));
            return null;
        } finally {
            setCatalogLoading(false);
        }
    }, [agreement]);

    const loadOrderAndDelivery = useCallback(async () => {
        const created = await TransactionService.getOrder(inquiryId, agreementId);
        setOrder(created);
        if (created) {
            setDelivery(await TransactionService.getDelivery(inquiryId, agreementId).catch(() => null));
        }
        return created;
    }, [inquiryId, agreementId]);

    /* ---------- Ouverture : catalogue, checkout, dernier paiement, commande ---------- */
    useEffect(() => {
        if (!open || !inquiryId || !agreementId) {
            return undefined;
        }
        let cancelled = false;
        idempotencyKeyRef.current = newIdempotencyKey();
        settledRef.current = null;
        setError(null);
        setPayment(null);
        setLastFailedPayment(null);
        setOrder(null);
        setDelivery(null);
        setQuotes(null);
        setQuotesError(null);
        setSelectedOptionId(null);
        setPhone("");
        setInitializing(true);

        Promise.allSettled([
            loadCatalog(),
            TransactionService.getCheckout(inquiryId, agreementId),
            TransactionService.getLatestPayment(inquiryId, agreementId),
            loadOrderAndDelivery()
        ]).then(([options, checkout, latest, created]) => {
            if (cancelled) {
                return;
            }
            const saved = checkout.status === "fulfilled" ? checkout.value : null;
            setDeliveryType(saved?.deliveryType || null);
            setPaymentMethod(saved?.paymentMethod || null);
            setOperator(saved?.mobileOperator || null);
            setAddress(addressFrom(saved?.deliveryAddress, options.status === "fulfilled" ? options.value?.country : null));

            if (latest.status === "fulfilled" && latest.value) {
                if (RETRYABLE_PAYMENT_STATUSES.includes(latest.value.status)) {
                    setLastFailedPayment(latest.value);
                } else {
                    setPayment(latest.value);
                }
            }
            const failures = [checkout, latest, created].filter(result => result.status === "rejected");
            if (failures.length) {
                setError(apiErrorMessage(failures[0].reason, "Impossible de charger l'état de cet achat."));
            }
        }).finally(() => {
            if (!cancelled) {
                setInitializing(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [open, inquiryId, agreementId, loadCatalog, loadOrderAndDelivery]);

    /* ---------- Devis de livraison ---------- */
    const requestQuotes = useCallback(async (type, deliveryAddress) => {
        if (!type || !inquiryId || !agreementId) {
            return;
        }
        setQuotesLoading(true);
        setQuotesError(null);
        setQuotes(null);
        setSelectedOptionId(null);
        try {
            const result = await TransactionService.getDeliveryQuotes(inquiryId, {
                agreementId,
                deliveryType: type,
                address: requiresAddress(type) ? deliveryAddress : null
            });
            setQuotes(result);
            if (result?.options?.length === 1) {
                setSelectedOptionId(result.options[0].id);
            }
        } catch (requestError) {
            setQuotesError(apiErrorMessage(requestError, "Impossible de calculer la livraison."));
        } finally {
            setQuotesLoading(false);
        }
    }, [inquiryId, agreementId]);

    const showJourney = open && !initializing && isBuyer && !order && !payment;

    /* Retrait : chiffrage immédiat (point de retrait + montant). */
    useEffect(() => {
        if (showJourney && deliveryType === "DELIVERY_BY_BUYER" && !quotes && !quotesLoading) {
            requestQuotes("DELIVERY_BY_BUYER", null);
        }
    }, [showJourney, deliveryType, quotes, quotesLoading, requestQuotes]);

    const handleDeliveryTypeChange = type => {
        setDeliveryType(type);
        setQuotes(null);
        setQuotesError(null);
        setSelectedOptionId(null);
        if (type !== "DELIVERY_BY_BUYER" && paymentMethod === "CASH") {
            setPaymentMethod(null);
        }
    };

    const handleAddressChange = next => {
        setAddress(next);
        // Un devis est calculé pour UNE adresse : il faut le recalculer.
        if (quotes && requiresAddress(deliveryType)) {
            setQuotes(null);
            setSelectedOptionId(null);
        }
    };

    const handlePaymentMethodChange = method => {
        setPaymentMethod(method);
        if (method !== "MOBILE_MONEY") {
            setOperator(null);
        }
    };

    /* ---------- Suivi du paiement tant qu'il est en cours ---------- */
    useEffect(() => {
        if (!open || !payment || !ACTIVE_PAYMENT_STATUSES.includes(payment.status) || !inquiryId) {
            return undefined;
        }
        const timer = setInterval(() => {
            TransactionService.getPayment(inquiryId, payment.paymentId)
                .then(setPayment)
                .catch(() => {
                    /* nouvel essai au prochain intervalle */
                });
        }, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [open, payment, inquiryId]);

    /* Paiement confirmé par le serveur : commande + suivi de livraison + rafraîchissement parent. */
    useEffect(() => {
        if (payment?.status !== "SUCCEEDED" || settledRef.current === payment.paymentId) {
            return;
        }
        settledRef.current = payment.paymentId;
        onPaymentSettled?.(payment);
        loadOrderAndDelivery().catch(requestError =>
            setError(apiErrorMessage(requestError, "Paiement confirmé, mais la commande n'a pas pu être chargée.")));
    }, [payment, onPaymentSettled, loadOrderAndDelivery]);

    /* ---------- Récapitulatif (montants backend) ---------- */
    const selectedOption = useMemo(
        () => quotes?.options?.find(option => option.id === selectedOptionId) ?? null,
        [quotes, selectedOptionId]
    );
    const summary = useMemo(() => {
        if (deliveryType === "DELIVERY_BY_BUYER" && quotes && !quotes.requiresOption) {
            return { productAmount: quotes.productAmount, deliveryCost: quotes.deliveryCost, totalAmount: quotes.totalAmount };
        }
        if (selectedOption) {
            return {
                productAmount: selectedOption.productAmount,
                deliveryCost: selectedOption.deliveryCost,
                totalAmount: selectedOption.totalAmount
            };
        }
        return { productAmount: quotes?.productAmount ?? agreement?.productAmount, deliveryCost: null, totalAmount: null };
    }, [deliveryType, quotes, selectedOption, agreement]);

    /* ---------- Validation explicite avant paiement ---------- */
    const blockingReason = useMemo(() => {
        if (!deliveryType) {
            return "Choisissez un mode de livraison.";
        }
        if (requiresAddress(deliveryType)) {
            const missing = missingAddressFields(address);
            if (missing.length) {
                return `Complétez l'adresse de livraison : ${missing.join(", ")}.`;
            }
            if (!quotes) {
                return deliveryType === "DELIVERY_BY_PLATFORM"
                    ? "Calculez les frais de livraison."
                    : "Affichez les partenaires disponibles.";
            }
            if (!selectedOption) {
                return "Choisissez une option de livraison.";
            }
        } else if (!quotes) {
            return "Chargement du retrait en cours...";
        }
        if (!paymentMethod) {
            return "Choisissez un moyen de paiement.";
        }
        const method = catalog?.methods?.find(item => item.method === paymentMethod);
        if (!method?.available) {
            return catalog?.paymentServiceAvailable === false
                ? "Le service de paiement n'est pas accessible : réessayez ou choisissez un autre moyen de paiement."
                : "Ce moyen de paiement n'est pas disponible.";
        }
        if (!method.allowedDeliveryTypes?.includes(deliveryType)) {
            return "Le paiement en espèces n'est possible qu'avec le retrait au siège.";
        }
        if (paymentMethod === "MOBILE_MONEY") {
            return mobileMoneyError({ operator, phone, phoneRule: catalog?.phone });
        }
        return null;
    }, [deliveryType, address, quotes, selectedOption, paymentMethod, catalog, operator, phone]);

    /* ---------- Payer : checkout puis paiement ---------- */
    const handlePay = async () => {
        if (blockingReason || submitting) {
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await TransactionService.confirmCheckout(inquiryId, {
                agreementId,
                deliveryType,
                deliveryOptionId: requiresAddress(deliveryType) ? selectedOptionId : null,
                paymentMethod,
                mobileOperator: paymentMethod === "MOBILE_MONEY" ? operator : null
            });
            const result = await TransactionService.initiatePayment(
                inquiryId,
                {
                    agreementId,
                    phoneNumber: paymentMethod === "MOBILE_MONEY"
                        ? `${catalog?.phone?.dialCode ?? ""}${phone.replace(/[\s.-]/g, "")}`
                        : null
                },
                idempotencyKeyRef.current
            );
            setPayment(result);
            setLastFailedPayment(null);
            if (result?.nextAction?.type === "REDIRECT" && result.nextAction.url
                && ACTIVE_PAYMENT_STATUSES.includes(result.status)) {
                window.location.assign(result.nextAction.url);
            }
        } catch (requestError) {
            setError(apiErrorMessage(requestError, "Impossible de finaliser le paiement."));
        } finally {
            setSubmitting(false);
        }
    };

    /** Nouvelle tentative après un échec : nouvelle clé, mêmes choix pré-remplis. */
    const handleRetry = () => {
        idempotencyKeyRef.current = newIdempotencyKey();
        setLastFailedPayment(payment);
        setPayment(null);
        setQuotes(null);
        setSelectedOptionId(null);
        setError(null);
    };

    const renderJourney = () => (
        <Space orientation="vertical" size="large" style={{ width: "100%" }}>
            {lastFailedPayment && (
                <Alert
                    type="warning"
                    showIcon
                    title="Le dernier paiement n'a pas abouti"
                    description="Aucun montant n'a été débité. Vérifiez vos choix puis réessayez : l'accord reste valable."
                />
            )}
            <DeliverySection
                deliveryType={deliveryType}
                onDeliveryTypeChange={handleDeliveryTypeChange}
                address={address}
                onAddressChange={handleAddressChange}
                quotes={quotes}
                quotesLoading={quotesLoading}
                quotesError={quotesError}
                onRequestQuotes={() => requestQuotes(deliveryType, address)}
                selectedOptionId={selectedOptionId}
                onSelectOption={setSelectedOptionId}
                disabled={submitting}
            />
            <OrderSummary {...summary} currency={currency} />
            <PaymentMethodSection
                catalog={catalog}
                catalogLoading={catalogLoading}
                catalogError={catalogError}
                deliveryType={deliveryType}
                paymentMethod={paymentMethod}
                onPaymentMethodChange={handlePaymentMethodChange}
                operator={operator}
                onOperatorChange={setOperator}
                phone={phone}
                onPhoneChange={setPhone}
                onRetryCatalog={loadCatalog}
                disabled={submitting}
            />
            {blockingReason && <Alert type="info" showIcon title={blockingReason} />}
            <Button
                type="primary"
                size="large"
                block
                loading={submitting}
                disabled={Boolean(blockingReason)}
                onClick={handlePay}
            >
                {paymentMethod === "CASH"
                    ? "Confirmer le paiement au siège"
                    : `Payer ${summary.totalAmount !== null && summary.totalAmount !== undefined ? formatAmount(summary.totalAmount, currency) : ""}`}
            </Button>
        </Space>
    );

    const renderSellerView = () => {
        const current = payment || lastFailedPayment;
        const status = current ? PAYMENT_STATUS_LABELS[current.status] : null;
        return (
            <Alert
                type="info"
                showIcon
                title="En attente du paiement de l'acheteur"
                description={status
                    ? <span>Dernière tentative : <Tag color={status.color}>{status.label}</Tag></span>
                    : "L'acheteur n'a pas encore choisi la livraison et le paiement."}
            />
        );
    };

    return (
        <Drawer
            title={order ? "Commande" : "Livraison et paiement"}
            placement="right"
            size={620}
            open={open}
            onClose={submitting ? undefined : onClose}
            destroyOnClose
            maskClosable={!submitting}
        >
            {agreement && (
                <Space orientation="vertical" size="large" style={{ width: "100%" }}>
                    {!inquiryId && (
                        <Alert type="error" showIcon title="Demande d'origine introuvable pour cet accord." />
                    )}
                    {error && <Alert type="error" showIcon title="Opération impossible" description={error} />}
                    {initializing && <Spin description="Préparation..." />}
                    {!initializing && order && <OrderConfirmation order={order} delivery={delivery} onClose={onClose} />}
                    {!initializing && !order && !isBuyer && renderSellerView()}
                    {!initializing && !order && isBuyer && payment && (
                        <PaymentStatusPanel payment={payment} order={order} delivery={delivery}
                            onRetry={handleRetry} onClose={onClose} />
                    )}
                    {showJourney && renderJourney()}
                </Space>
            )}
        </Drawer>
    );
};

PaymentFinalizationDrawer.propTypes = {
    open: PropTypes.bool,
    agreement: PropTypes.shape({
        id: PropTypes.string,
        inquiryId: PropTypes.string,
        buyerId: PropTypes.string,
        sellerId: PropTypes.string,
        status: PropTypes.string,
        currency: PropTypes.string,
        productAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    }),
    currentUserId: PropTypes.string,
    onClose: PropTypes.func,
    onPaymentSettled: PropTypes.func
};

export default PaymentFinalizationDrawer;
